/**
 * Symbol Performance Tab Component
 * Comprehensive analysis of trading performance by symbol/asset
 */

import React, { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { DataTable } from "../../../../components/ui/data-table";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { Button } from "../../../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "../../../../components/ui/popover";
import { cn } from "@/lib/utils";
import { Card as UiCard, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { StatisticsCardHeadline, STATS_FONT_FAMILY } from "./statisticsCardHeadline";
import { STATISTICS_NO_DATA_TIMEFRAME_MESSAGE } from "./statisticsChartTooltip";
import { StatisticsTabEmptyState, isStatisticsFetchPending, StatisticsTabLoadingState } from "./statisticsEmptyState";
import { statisticsShadcnCardClassName, statisticsShadcnCardContentClassName, statisticsShadcnCardHeaderClassName, statisticsShadcnBarChartAreaClassName } from "./statisticsShadcnCardShell";
import SignedPnLBarChart from "./StatsSignedPnlBarChart";
import {
  PNL_NEGATIVE_COLOR,
  PNL_POSITIVE_COLOR,
  pnlBarFill,
  SIGNED_PNL_BAR_CHART_MARGIN_COMPACT,
  type SignedPnLBarDatum,
} from "./statsSignedPnlBarUtils";
import { TradeStatisticsFilters } from "../../../../types/trade";
import { useSymbolStatistics } from "../../../../hooks/useTradeStatistics";
import { useStatisticsWidgetErrorGate } from "../../../../hooks/useStatisticsWidgetErrorGate";
import { StatisticsInlineError } from "../../../../components/errors/StatisticsInlineError";

interface SymbolPerformanceTabProps {
  filters?: TradeStatisticsFilters;
  /** When provided, sections show Selected period and Comparison period cards */
  comparisonFilters?: TradeStatisticsFilters;
}

interface SymbolStats {
  symbol: string;
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  winRate: number;
  totalPnL: number;
  averagePnL: number;
  averageWin: number;
  averageLoss: number;
  largestWin: number;
  largestLoss: number;
  totalReturnPercent: number;
  buyTrades: number;
  sellTrades: number;
}

/** Best / worst summary cards rank by total P&L (descending), not win rate. */
function compareSymbolsByTotalPnL(a: SymbolStats, b: SymbolStats): number {
  return b.totalPnL - a.totalPnL;
}

function rankSymbolsByTotalPnL(symbols: SymbolStats[]): SymbolStats[] {
  return [...symbols].sort(compareSymbolsByTotalPnL);
}

function bestSymbolByTotalPnL(symbols: SymbolStats[]): SymbolStats | undefined {
  const ranked = rankSymbolsByTotalPnL(symbols);
  return ranked[0];
}

function worstSymbolByTotalPnL(symbols: SymbolStats[]): SymbolStats | undefined {
  const ranked = rankSymbolsByTotalPnL(symbols);
  return ranked.length > 0 ? ranked[ranked.length - 1] : undefined;
}

const SYMBOL_CHART_SQUARE_CLASS =
  "h-full min-h-0 w-full !aspect-auto [&_.recharts-wrapper]:!overflow-visible [&_.recharts-surface]:overflow-visible";

const SYMBOL_CHART_STACKED_CLASS =
  "aspect-auto h-[350px] w-full [&_.recharts-wrapper]:!overflow-visible [&_.recharts-surface]:overflow-visible";

const SYMBOL_CHART_SHARED_PROPS = {
  chartMargin: SIGNED_PNL_BAR_CHART_MARGIN_COMPACT,
  yAxisWidth: 56,
} as const;

const SYMBOL_SQUARE_CHART_PROPS = {
  ...SYMBOL_CHART_SHARED_PROPS,
  className: SYMBOL_CHART_SQUARE_CLASS,
} as const;

const SYMBOL_PREVIEW_COUNT = 3;

const SYMBOL_STACKED_CHART_PROPS = {
  ...SYMBOL_CHART_SHARED_PROPS,
  className: SYMBOL_CHART_STACKED_CLASS,
} as const;

const SELECTED_PERIOD_PANEL_CLASS =
  "mb-6 rounded-lg border-2 border-[color:var(--brand-tropical-surf)] bg-card p-4";

const COMPARISON_PERIOD_PANEL_CLASS =
  "mb-6 rounded-lg border-2 border-[color:var(--brand-comparison-period)] bg-card p-4";

/** Tessera brand blues — @see src/Branding/brand-guidelines.md */
const BRAND_BLUE_SPECTRUM = ["#38BBD1", "#73E6F0", "#3EEAEC", "#96FBF7"] as const;

function brandBlueBarFill(index: number): string {
  return BRAND_BLUE_SPECTRUM[index % BRAND_BLUE_SPECTRUM.length];
}

function toPnlChartData(symbols: SymbolStats[]): SignedPnLBarDatum[] {
  return symbols.slice(0, 10).map((s) => ({
    label: s.symbol,
    pnl: s.totalPnL,
    fill: pnlBarFill(s.totalPnL),
  }));
}

function toWinRateChartData(symbols: SymbolStats[]): SignedPnLBarDatum[] {
  return symbols.slice(0, 10).map((s, index) => ({
    label: s.symbol,
    pnl: s.winRate,
    fill: brandBlueBarFill(index),
  }));
}

function toTradesChartData(symbols: SymbolStats[]): SignedPnLBarDatum[] {
  return [...symbols]
    .sort((a, b) => b.totalTrades - a.totalTrades)
    .slice(0, 10)
    .map((s, index) => ({
      label: s.symbol,
      pnl: s.totalTrades,
      fill: brandBlueBarFill(index),
    }));
}

const formatWinRateAxis = (value: number): string => `${value}%`;
const formatTradeCountAxis = (value: number): string =>
  Number.isInteger(value) ? String(value) : String(Math.round(value));

function SymbolHeaderSummaryStat({
  label,
  value,
  valueColor,
  withLeadingBorder = false,
  valueClassName,
}: {
  label: string;
  value: string;
  valueColor?: string;
  withLeadingBorder?: boolean;
  valueClassName?: string;
}) {
  return (
    <div
      className={cn(
        "relative flex min-w-[140px] flex-1 flex-col justify-center gap-1 border-t border-border px-[22px] py-4 text-left sm:border-t-0 sm:py-5",
        withLeadingBorder && "sm:border-l"
      )}
      style={{ fontFamily: STATS_FONT_FAMILY }}
    >
      <span className="text-xs text-muted-foreground">{label}</span>
      <span
        className={cn(
          "text-lg leading-none tabular-nums tracking-tight",
          valueColor
            ? "font-semibold"
            : "font-[family-name:var(--font-primary)] font-medium text-muted-foreground",
          valueClassName
        )}
        style={valueColor ? { color: valueColor } : undefined}
        {...(valueColor ? { "data-trade-log-pnl": true } : {})}
      >
        {value}
      </span>
    </div>
  );
}

const SymbolPerformanceTab: React.FC<SymbolPerformanceTabProps> = ({ filters, comparisonFilters }) => {
  const { formatMoney } = useDisplayCurrency();
  const hasComparison = Boolean(comparisonFilters);

  // Fetch symbol statistics from backend API
  const { symbolStats: backendSymbolStats, error, errorAction } = useSymbolStatistics(filters);
  const { symbolStats: backendComparisonSymbolStats, error: comparisonError, errorAction: comparisonErrorAction } =
    useSymbolStatistics(comparisonFilters, { enabled: hasComparison });
  const widgetGateError = error || (hasComparison ? comparisonError : null);
  const widgetGateAction =
    error != null ? errorAction : hasComparison && comparisonError != null ? comparisonErrorAction : null;
  const { showWidgetError, widgetError, widgetAction } = useStatisticsWidgetErrorGate(
    widgetGateError,
    widgetGateAction
  );

  // Convert backend format to component format
  const symbolStats = useMemo(() => {
    if (!backendSymbolStats || !backendSymbolStats.symbols) {
      return [];
    }
    const mapped = backendSymbolStats.symbols.map((s) => ({
      symbol: s.symbol,
      totalTrades: s.total_trades,
      winningTrades: s.winning_trades,
      losingTrades: s.losing_trades,
      winRate: s.win_rate,
      totalPnL: s.total_pnl,
      averagePnL: s.average_pnl,
      averageWin: s.average_win ?? 0,
      averageLoss: s.average_loss ?? 0,
      largestWin: s.best_trade,
      largestLoss: s.worst_trade,
      totalReturnPercent: s.total_return_percent || 0,
      buyTrades: s.buy_trades || 0,
      sellTrades: s.sell_trades || 0,
    }));
    return rankSymbolsByTotalPnL(mapped);
  }, [backendSymbolStats]);

  const comparisonSymbolStats = useMemo(() => {
    if (!backendComparisonSymbolStats || !backendComparisonSymbolStats.symbols) return [];
    const mapped = backendComparisonSymbolStats.symbols.map((s) => ({
      symbol: s.symbol,
      totalTrades: s.total_trades,
      winningTrades: s.winning_trades,
      losingTrades: s.losing_trades,
      winRate: s.win_rate,
      totalPnL: s.total_pnl,
      averagePnL: s.average_pnl,
      averageWin: s.average_win ?? 0,
      averageLoss: s.average_loss ?? 0,
      largestWin: s.best_trade,
      largestLoss: s.worst_trade,
      totalReturnPercent: s.total_return_percent || 0,
      buyTrades: s.buy_trades || 0,
      sellTrades: s.sell_trades || 0,
    }));
    return rankSymbolsByTotalPnL(mapped);
  }, [backendComparisonSymbolStats]);

  // Format currency

  // Format percentage
  const formatPercent = (value: number): string => {
    return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
  };

  const pnlChartData = useMemo(() => toPnlChartData(symbolStats), [symbolStats]);
  const winRateChartData = useMemo(() => toWinRateChartData(symbolStats), [symbolStats]);
  const tradesChartData = useMemo(() => toTradesChartData(symbolStats), [symbolStats]);
  const comparisonPnlChartData = useMemo(
    () => toPnlChartData(comparisonSymbolStats),
    [comparisonSymbolStats]
  );
  const comparisonWinRateChartData = useMemo(
    () => toWinRateChartData(comparisonSymbolStats),
    [comparisonSymbolStats]
  );
  const comparisonTradesChartData = useMemo(
    () => toTradesChartData(comparisonSymbolStats),
    [comparisonSymbolStats]
  );

  const breakdownColumns: ColumnDef<SymbolStats, unknown>[] = useMemo(() => [
    {
      accessorKey: "symbol",
      header: "Symbol",
      enableSorting: false,
      cell: ({ row }) => (
        <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-muted-foreground">
          {row.original.symbol}
        </span>
      ),
    },
    {
      accessorKey: "totalTrades",
      header: "Trades",
      enableSorting: false,
      cell: ({ row }) => (
        <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-muted-foreground">
          {row.original.totalTrades}
        </span>
      ),
    },
    {
      accessorKey: "buyTrades",
      header: "Long",
      enableSorting: false,
      cell: ({ row }) => (
        <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-muted-foreground">
          {row.original.buyTrades}
        </span>
      ),
    },
    {
      accessorKey: "sellTrades",
      header: "Short",
      enableSorting: false,
      cell: ({ row }) => (
        <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-muted-foreground">
          {row.original.sellTrades}
        </span>
      ),
    },
    {
      accessorKey: "winRate",
      header: "Win Rate",
      enableSorting: false,
      cell: ({ row }) => {
        const closedTrades = row.original.winningTrades + row.original.losingTrades;
        return (
          <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-muted-foreground">
            {closedTrades > 0 ? `${row.original.winRate.toFixed(2)}%` : "N/A"}
          </span>
        );
      },
    },
    {
      accessorKey: "totalPnL",
      header: "Total P&L",
      enableSorting: false,
      cell: ({ row }) => (
        <span
          data-trade-log-pnl
          className="font-[family-name:var(--font-primary)] text-sm font-medium"
          style={{ color: row.original.totalPnL >= 0 ? PNL_POSITIVE_COLOR : PNL_NEGATIVE_COLOR }}
        >
          {formatMoney(row.original.totalPnL)}
        </span>
      ),
    },
    {
      accessorKey: "averagePnL",
      header: "Avg P&L",
      enableSorting: false,
      cell: ({ row }) => {
        const closedTrades = row.original.winningTrades + row.original.losingTrades;
        return (
          <span
            data-trade-log-pnl
            className="font-[family-name:var(--font-primary)] text-sm font-medium"
            style={{ color: row.original.averagePnL >= 0 ? PNL_POSITIVE_COLOR : PNL_NEGATIVE_COLOR }}
          >
            {closedTrades > 0 ? formatMoney(row.original.averagePnL) : "N/A"}
          </span>
        );
      },
    },
    {
      accessorKey: "averageWin",
      header: "Avg Win",
      enableSorting: false,
      cell: ({ row }) => (
        <span
          data-trade-log-pnl
          className="font-[family-name:var(--font-primary)] text-sm font-medium"
          style={{ color: PNL_POSITIVE_COLOR }}
        >
          {row.original.winningTrades > 0 ? formatMoney(row.original.averageWin) : "N/A"}
        </span>
      ),
    },
    {
      accessorKey: "averageLoss",
      header: "Avg Loss",
      enableSorting: false,
      cell: ({ row }) => (
        <span
          data-trade-log-pnl
          className="font-[family-name:var(--font-primary)] text-sm font-medium"
          style={{ color: PNL_NEGATIVE_COLOR }}
        >
          {row.original.losingTrades > 0 ? formatMoney(-row.original.averageLoss) : "N/A"}
        </span>
      ),
    },
    {
      accessorKey: "largestWin",
      header: "Largest Win",
      enableSorting: false,
      cell: ({ row }) => (
        <span
          data-trade-log-pnl
          className="font-[family-name:var(--font-primary)] text-sm font-medium"
          style={{ color: PNL_POSITIVE_COLOR }}
        >
          {row.original.largestWin > 0 ? formatMoney(row.original.largestWin) : "N/A"}
        </span>
      ),
    },
    {
      accessorKey: "largestLoss",
      header: "Largest Loss",
      enableSorting: false,
      cell: ({ row }) => (
        <span
          data-trade-log-pnl
          className="font-[family-name:var(--font-primary)] text-sm font-medium"
          style={{ color: PNL_NEGATIVE_COLOR }}
        >
          {row.original.largestLoss < 0 ? formatMoney(row.original.largestLoss) : "N/A"}
        </span>
      ),
    },
  ], [formatMoney]);

  if (isStatisticsFetchPending(backendSymbolStats, error)) {
    return <StatisticsTabLoadingState />;
  }

  if (hasComparison && isStatisticsFetchPending(backendComparisonSymbolStats, comparisonError)) {
    return <StatisticsTabLoadingState />;
  }

  if (showWidgetError && widgetError) {
    return <StatisticsInlineError message={widgetError} action={widgetAction ?? undefined} />;
  }

  if (!hasComparison && symbolStats.length === 0) {
    return <StatisticsTabEmptyState />;
  }

  if (hasComparison && symbolStats.length === 0 && comparisonSymbolStats.length === 0) {
    return <StatisticsTabEmptyState />;
  }

  const totalTrades = symbolStats.reduce((sum, s) => sum + s.totalTrades, 0);
  const comparisonTotalTrades = comparisonSymbolStats.reduce((sum, s) => sum + s.totalTrades, 0);

  const SummaryCard: React.FC<{
    title: string;
    description: string;
    value?: string;
    pnlAmount?: number;
    badge?: string;
    symbols?: string[];
  }> = ({ title, description, value, pnlAmount, badge, symbols }) => {
    const renderSymbolBadge = (symbol: string) => (
      <span key={symbol} data-symbol-summary-chip>
        {symbol}
      </span>
    );

    const hasSymbolList = Boolean(symbols && symbols.length > 0);
    const pnlColor =
      pnlAmount !== undefined
        ? pnlAmount >= 0
          ? PNL_POSITIVE_COLOR
          : PNL_NEGATIVE_COLOR
        : undefined;

    return (
      <UiCard
        data-statistics-shadcn-card
        className={cn(statisticsShadcnCardClassName)}
        style={{ fontFamily: STATS_FONT_FAMILY }}
      >
        <CardHeader className="!flex w-full flex-col items-stretch gap-0 rounded-none border-b border-border !p-0 sm:flex-row [&.border-b]:!pb-0">
          <div className="flex flex-1 flex-col justify-center gap-1 px-[22px] py-4 sm:py-5">
            <StatisticsCardHeadline>{title}</StatisticsCardHeadline>
            {description ? (
              <CardDescription className="text-xs" style={{ fontFamily: STATS_FONT_FAMILY }}>
                {description}
              </CardDescription>
            ) : null}
          </div>
          <div className="flex w-full sm:w-auto">
            {hasSymbolList ? (
              <div
                className="flex min-w-[140px] flex-1 flex-wrap items-center justify-start gap-1 border-t border-border px-[22px] py-4 sm:border-t-0 sm:border-l sm:py-5"
                data-statistics-symbol-summary=""
              >
                {symbols!.slice(0, SYMBOL_PREVIEW_COUNT).map(renderSymbolBadge)}

                {symbols!.length > SYMBOL_PREVIEW_COUNT && (
                  <span className="inline-flex shrink-0 leading-none">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="xs"
                          data-font="primary"
                          data-symbol-summary-popover-trigger=""
                          className="shrink-0 normal-case tracking-normal"
                        >
                          +{symbols!.length - SYMBOL_PREVIEW_COUNT} more
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="end"
                        className="w-72 gap-0 bg-popover p-3 font-[family-name:var(--font-ibm-plex)]"
                      >
                        <PopoverHeader className="gap-0 pb-2">
                          <PopoverTitle className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            All symbols ({symbols!.length})
                          </PopoverTitle>
                        </PopoverHeader>
                        <div
                          data-symbol-summary-popover
                          className="flex max-h-60 flex-wrap gap-1.5 overflow-y-auto pr-0.5"
                        >
                          {symbols!.map(renderSymbolBadge)}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </span>
                )}
              </div>
            ) : (
              <>
                {badge ? (
                  <SymbolHeaderSummaryStat
                    label="Symbol"
                    value={badge}
                    valueClassName="whitespace-nowrap uppercase"
                  />
                ) : null}
                {value ? (
                  <SymbolHeaderSummaryStat
                    label="Total P&L"
                    value={value}
                    valueColor={pnlColor}
                    withLeadingBorder={Boolean(badge)}
                  />
                ) : null}
              </>
            )}
          </div>
        </CardHeader>
      </UiCard>
    );
  };

  const renderSymbolChartCard = (title: string, chart: React.ReactNode) => (
    <div className="mb-0 aspect-square w-full sm:mb-[26px] lg:mb-0">
      <UiCard
        data-statistics-shadcn-card
        className={cn(statisticsShadcnCardClassName, "h-full")}
        style={{ fontFamily: STATS_FONT_FAMILY }}
      >
        <CardHeader className={statisticsShadcnCardHeaderClassName}>
          <StatisticsCardHeadline mb={{ base: 1, md: 2 }}>{title}</StatisticsCardHeadline>
        </CardHeader>
        <CardContent className={cn(statisticsShadcnCardContentClassName, "flex min-h-0 flex-1 flex-col pt-2")}>
          <div className={cn(statisticsShadcnBarChartAreaClassName, "flex min-h-0 flex-1")}>{chart}</div>
        </CardContent>
      </UiCard>
    </div>
  );

  const renderTopThreeCards = (stats: SymbolStats[], totalTr: number) => {
    const best = bestSymbolByTotalPnL(stats);
    const worst = worstSymbolByTotalPnL(stats);
    if (!best || !worst) {
      return <StatisticsTabEmptyState compact />;
    }
    return (
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4">
        <SummaryCard
          title="Best Performing Symbol"
          description={`${best.totalTrades} trades • ${best.winRate.toFixed(2)}% win rate`}
          value={formatMoney(best.totalPnL)}
          pnlAmount={best.totalPnL}
          badge={best.symbol}
        />
        <SummaryCard
          title="Worst Performing Symbol"
          description={`${worst.totalTrades} trades • ${worst.winRate.toFixed(2)}% win rate`}
          value={formatMoney(worst.totalPnL)}
          pnlAmount={worst.totalPnL}
          badge={worst.symbol}
        />
        <SummaryCard
          title="Total Symbols Traded"
          description={`${stats.length} symbols · ${totalTr.toLocaleString()} trades`}
          symbols={stats.map((s) => s.symbol)}
        />
      </div>
    );
  };

  return (
    <div>
      {hasComparison ? (
        <>
          {/* Top three: Selected then Comparison */}
          <div className={SELECTED_PERIOD_PANEL_CLASS}>
            {renderTopThreeCards(symbolStats, totalTrades)}
          </div>
          <div className={COMPARISON_PERIOD_PANEL_CLASS}>
            {renderTopThreeCards(comparisonSymbolStats, comparisonTotalTrades)}
          </div>

          {/* Total P&L by Symbol: Selected then Comparison */}
          <div className={SELECTED_PERIOD_PANEL_CLASS}>
            <StatisticsCardHeadline mb="6px">Total P&L by Symbol</StatisticsCardHeadline>
            <div className="mt-2 w-full px-1 pb-1 sm:px-2 lg:px-3">
              {pnlChartData.length > 0 ? (
                <SignedPnLBarChart data={pnlChartData} symmetricYAxis {...SYMBOL_STACKED_CHART_PROPS} />
              ) : null}
            </div>
          </div>
          <div className={COMPARISON_PERIOD_PANEL_CLASS}>
            <StatisticsCardHeadline mb="6px">Total P&L by Symbol</StatisticsCardHeadline>
            <div className="mt-2 w-full px-1 pb-1 sm:px-2 lg:px-3">
              {comparisonPnlChartData.length > 0 ? (
                <SignedPnLBarChart
                  data={comparisonPnlChartData}
                  symmetricYAxis
                  {...SYMBOL_STACKED_CHART_PROPS}
                />
              ) : null}
            </div>
          </div>

          {/* Win Rate by Symbol: Selected then Comparison */}
          <div className={SELECTED_PERIOD_PANEL_CLASS}>
            <StatisticsCardHeadline mb="6px">Win Rate by Symbol</StatisticsCardHeadline>
            <div className="mt-2 w-full px-1 pb-1 sm:px-2 lg:px-3">
              {winRateChartData.length > 0 ? (
                <SignedPnLBarChart
                  data={winRateChartData}
                  yDomain={[0, 100]}
                  formatYAxis={formatWinRateAxis}
                  tooltipMetricLabel="Win Rate"
                  {...SYMBOL_STACKED_CHART_PROPS}
                />
              ) : null}
            </div>
          </div>
          <div className={COMPARISON_PERIOD_PANEL_CLASS}>
            <StatisticsCardHeadline mb="6px">Win Rate by Symbol</StatisticsCardHeadline>
            <div className="mt-2 w-full px-1 pb-1 sm:px-2 lg:px-3">
              {comparisonWinRateChartData.length > 0 ? (
                <SignedPnLBarChart
                  data={comparisonWinRateChartData}
                  yDomain={[0, 100]}
                  formatYAxis={formatWinRateAxis}
                  tooltipMetricLabel="Win Rate"
                  {...SYMBOL_STACKED_CHART_PROPS}
                />
              ) : null}
            </div>
          </div>

          {/* Trades by Symbol: Selected then Comparison */}
          <div className={SELECTED_PERIOD_PANEL_CLASS}>
            <StatisticsCardHeadline mb="6px">Trades by Symbol</StatisticsCardHeadline>
            <div className="mt-2 w-full px-1 pb-1 sm:px-2 lg:px-3">
              {tradesChartData.length > 0 ? (
                <SignedPnLBarChart
                  data={tradesChartData}
                  formatYAxis={formatTradeCountAxis}
                  tooltipMetricLabel="Trades"
                  {...SYMBOL_STACKED_CHART_PROPS}
                />
              ) : null}
            </div>
          </div>
          <div className={COMPARISON_PERIOD_PANEL_CLASS}>
            <StatisticsCardHeadline mb="6px">Trades by Symbol</StatisticsCardHeadline>
            <div className="mt-2 w-full px-1 pb-1 sm:px-2 lg:px-3">
              {comparisonTradesChartData.length > 0 ? (
                <SignedPnLBarChart
                  data={comparisonTradesChartData}
                  formatYAxis={formatTradeCountAxis}
                  tooltipMetricLabel="Trades"
                  {...SYMBOL_STACKED_CHART_PROPS}
                />
              ) : null}
            </div>
          </div>

          {/* Symbol Performance Breakdown: Selected then Comparison */}
          <div className={SELECTED_PERIOD_PANEL_CLASS}>
            <div className="mb-4">
              <StatisticsCardHeadline>Symbol Performance Breakdown</StatisticsCardHeadline>
            </div>
            <DataTable
              columns={breakdownColumns}
              data={symbolStats}
              showPagination={false}
              emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
            />
          </div>
          <div className={COMPARISON_PERIOD_PANEL_CLASS}>
            <div className="mb-4">
              <StatisticsCardHeadline>Symbol Performance Breakdown</StatisticsCardHeadline>
            </div>
            <DataTable
              columns={breakdownColumns}
              data={comparisonSymbolStats}
              showPagination={false}
              emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
            />
          </div>
        </>
      ) : (
        <>
          <div className="mb-6">{renderTopThreeCards(symbolStats, totalTrades)}</div>

          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            {renderSymbolChartCard(
              "Total P&L by Symbol",
              pnlChartData.length > 0 ? (
                <SignedPnLBarChart data={pnlChartData} symmetricYAxis {...SYMBOL_SQUARE_CHART_PROPS} />
              ) : null
            )}
            {renderSymbolChartCard(
              "Win Rate by Symbol",
              winRateChartData.length > 0 ? (
                <SignedPnLBarChart
                  data={winRateChartData}
                  yDomain={[0, 100]}
                  formatYAxis={formatWinRateAxis}
                  tooltipMetricLabel="Win Rate"
                  {...SYMBOL_SQUARE_CHART_PROPS}
                />
              ) : null
            )}
            {renderSymbolChartCard(
              "Trades by Symbol",
              tradesChartData.length > 0 ? (
                <SignedPnLBarChart
                  data={tradesChartData}
                  formatYAxis={formatTradeCountAxis}
                  tooltipMetricLabel="Trades"
                  {...SYMBOL_SQUARE_CHART_PROPS}
                />
              ) : null
            )}
          </div>

          <div data-statistics-shadcn-card className="rounded-[6px] border border-border bg-card p-4">
            <div className="mb-4">
              <StatisticsCardHeadline>Symbol Performance Breakdown</StatisticsCardHeadline>
            </div>
            <DataTable
              columns={breakdownColumns}
              data={symbolStats}
              showPagination={false}
              emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default SymbolPerformanceTab;

